import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import { Input } from "@arcadeai/design-system/components/ui/atoms/input";
import { Fingerprint, Plus, Search } from "lucide-react";
import { useMemo, useState } from "react";
import { ListLayout } from "@/components/custom/list-layout/list-layout";
import { EmptyResourceState } from "@/components/custom/resources/empty-resource-state";
import {
  ResourceContainer,
  type ResourceSection,
} from "@/components/custom/resources/resource-container";
import { ResourceViewSwitcher } from "@/components/custom/resources/resource-view-switcher";
import { useGetMcpGateways } from "../mcp-gateways/api/hooks";
import { useGetUserSources } from "./api/hooks";
import { UserSourcesTable } from "./components/table/user-sources-table";
import { useUserSourcesViewOptions } from "./components/table/user-sources-view-options";
import { UserSourceSheet } from "./components/user-source-sheet";
import type { UserSource } from "./types";
import { groupUserSourcesByBinding } from "./utils/group-by-binding";

function filterUserSources(items: UserSource[], term: string): UserSource[] {
  const query = term.trim().toLowerCase();
  if (!query) {
    return items;
  }
  return items.filter((userSource) => {
    const searchableText = [
      userSource.name,
      userSource.description,
      userSource.issuer,
      userSource.client_id,
      userSource.status,
    ]
      .join(" ")
      .toLowerCase();
    return searchableText.includes(query);
  });
}

export const UserSourcesList = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const { data } = useGetUserSources();
  const items = data.data.items;

  const gatewaysQuery = useGetMcpGateways("project");

  const gatewayCountMap = useMemo<Record<string, number>>(() => {
    const gateways = gatewaysQuery.data ?? [];
    const map: Record<string, number> = {};
    for (const gw of gateways) {
      if (
        gw.auth_type === "user_source" &&
        gw.user_source_id &&
        gw.status === "active"
      ) {
        map[gw.user_source_id] = (map[gw.user_source_id] ?? 0) + 1;
      }
    }
    return map;
  }, [gatewaysQuery.data]);

  const {
    columnVisibility,
    setColumnVisibility,
    component: ViewOptionsComponent,
  } = useUserSourcesViewOptions();

  const filtered = useMemo(
    () => filterUserSources(items, searchTerm),
    [items, searchTerm]
  );

  const sections: ResourceSection[] = useMemo(() => {
    const grouped = groupUserSourcesByBinding(filtered);
    const result: ResourceSection[] = [];
    let isFirstSection = true;

    if (grouped.project.length > 0) {
      result.push({
        binding: "project",
        itemsCount: grouped.project.length,
        resourceType: "user sources",
        defaultOpen: isFirstSection,
        children: (
          <UserSourcesTable
            columnVisibility={columnVisibility}
            gatewayCountMap={gatewayCountMap}
            gatewaysIsError={gatewaysQuery.isError}
            gatewaysIsPending={gatewaysQuery.isPending}
            items={grouped.project}
            onColumnVisibilityChange={setColumnVisibility}
          />
        ),
      });
      isFirstSection = false;
    }

    if (grouped.organization.length > 0) {
      result.push({
        binding: "tenant",
        itemsCount: grouped.organization.length,
        resourceType: "user sources",
        defaultOpen: isFirstSection,
        children: (
          <UserSourcesTable
            columnVisibility={{
              ...columnVisibility,
              actions: false,
              gateways_using: false,
            }}
            items={grouped.organization}
          />
        ),
      });
    }

    return result;
  }, [
    filtered,
    columnVisibility,
    setColumnVisibility,
    gatewayCountMap,
    gatewaysQuery.isError,
    gatewaysQuery.isPending,
  ]);

  return (
    <ListLayout
      description="Configure OpenID Connect identity providers for end-user authentication on your MCP gateways."
      filters={
        <>
          <div className="flex items-center gap-3">
            <div className="relative max-w-md">
              <Search className="absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="h-9 w-full pl-9"
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search user sources..."
                value={searchTerm}
              />
            </div>
            <div className="flex items-center gap-2">
              {ViewOptionsComponent}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <UserSourceSheet
              trigger={
                <Button size="sm">
                  <Plus className="mr-1 size-4" />
                  Create user source
                </Button>
              }
            />
            <ResourceViewSwitcher />
          </div>
        </>
      }
      title="User sources"
    >
      {sections.length > 0 ? (
        <ResourceContainer
          defaultActiveTab={
            sections[0] && `${sections[0].binding}-${sections[0].resourceType}`
          }
          sections={sections}
        />
      ) : (
        <EmptyResourceState
          description={
            items.length > 0
              ? "No user sources match your search."
              : "User sources let you bring your own OIDC identity provider. Create one to get started."
          }
          icon={items.length > 0 ? Search : Fingerprint}
          resourceType={
            items.length > 0 ? "matching user sources" : "user sources"
          }
          title={items.length > 0 ? undefined : "No user sources yet"}
        />
      )}
    </ListLayout>
  );
};
